import React, { useState } from "react";
import Form from "react-bootstrap/Form";
import Button from "react-bootstrap/Button";
import styled from "styled-components";
import { useLocation } from "react-router";
import Api from "../../../services/Api";
import SubPageBanner from "../../../common/component/SubPageBanner";
import { EMAIL_ADDRESS, PHONE_NUMBER } from "../../../constants/general";
import { useLogout } from "../../../hooks/useLogout";
import Box from "@mui/material/Box";
import { HOME_URL } from "../../HomePage/HomePage";
import Typography from "@mui/material/Typography";
import { Link } from "../../../components/Link";

const SCHEDULE_CALL_URL = "https://go.oncehub.com/samanthasnowden";

const OFF_CAMPUS_RESOURCES = [
  {
    label: "Off-Campus Properties Resources Drive",
    url: "https://drive.google.com/drive/u/0/folders/0BzEzvGMmH9nKb0ZKS2JISnp1bDg",
  },
  {
    label: "Off-Campus Properties Welcome & Training Video",
    url: "https://drive.google.com/file/d/1QLVOMyh9S8n7_fL_WNJBqI7ft0Y70vTN/view",
  },
];

const ON_CAMPUS_RESOURCES = [
  {
    label: "On-Campus Housing Resources Drive",
    url: "https://drive.google.com/drive/folders/0BzEzvGMmH9nKQVVrdjZncmNraEE",
  },
  {
    label: "On-Campus Housing Welcome & Training Video",
    url: "https://drive.google.com/file/d/1Jr8hq_vL95R1h9jdFm5ak9X8Q1_awyZU/view",
  },
];

export const RequestAccessContainer = () => {
  const location = useLocation();

  const [submitted, setSubmitted] = useState({
    submitted: false,
    failed: false,
  });

  const search = new URLSearchParams(location.search);

  const submitAccessRequest = async (event) => {
    event.preventDefault();
    event.stopPropagation();

    const form = event.currentTarget;
    if (form.checkValidity() === false) {
      return;
    }

    const values = event.target.elements;
    const success = await Api.requestAdminAccess(
      values.name.value,
      values.email.value,
      values.phoneNumber.value,
      search.get("openId"),
      values.organization.value
    );

    setSubmitted({ submitted: true, failed: !success });
  };

  const screen = submitted.failed ? <FailureScreen /> : <SuccessScreen email={search.get("email")} />;

  return (
    <>{!submitted.submitted ? <RequestForm submitAccessRequest={submitAccessRequest} search={search} /> : screen}</>
  );
};

export const REQUEST_ACCESS_URL = "/access";

const RequestForm = ({ submitAccessRequest, search }) => {
  const logout = useLogout();

  return (
    <div>
      <Typography gutterBottom variant="h5">
        Submit Your Registration Request
      </Typography>
      <Form onSubmit={submitAccessRequest}>
        <Form.Group controlId="name">
          <Form.Label>Name</Form.Label>
          <Form.Control placeholder="Enter name" />
        </Form.Group>
        <Form.Group controlId="email">
          <Form.Label>Email address</Form.Label>
          <Form.Control type="email" placeholder="Enter email" defaultValue={search.get("email")} disabled />
        </Form.Group>
        <Form.Group controlId="phoneNumber">
          <Form.Label>Phone number</Form.Label>
          <Form.Control type="tel" placeholder="Enter phone number" />
        </Form.Group>
        <Form.Group controlId="organization">
          <Form.Label>Organization</Form.Label>
          <Form.Control placeholder="Enter organization" />
        </Form.Group>
        <Button variant="primary" type="submit">
          Submit Registration Request
        </Button>
      </Form>
      <Box mt={2}>
        If you already submitted a request, you may be trying to submit with a different Google account now.{" "}
        <Link href={HOME_URL} onClick={logout}>
          Log out
        </Link>{" "}
        and log back in with the same Google you first submitted for.
      </Box>
    </div>
  );
};

const StatusHolder = styled.div`
  text-align: justify;
`;

const SuccessScreen = ({ email }) => (
  <>
    <Typography gutterBottom variant="h5">
      Network Access Pending
    </Typography>
    <Typography paragraph>
      We have received your request to access RoomSync Admin for {email}. As soon as we have verified your request to be
      an administrator of this organization, you will be notified and your access will be granted.
    </Typography>
    <Typography gutterBottom variant="h6">
      Schedule Training
    </Typography>
    <Typography paragraph>
      If you are new to RoomSync or just need a refresher, you can{" "}
      <Link href={SCHEDULE_CALL_URL}>schedule a training call.</Link>
    </Typography>
    <Typography gutterBottom variant="h6">
      Resources {"&"} Welcome Video
    </Typography>
    <Typography paragraph>
      We recommend visiting our admin resources Drive where you will find marketing and training tools. The Welcome{" "}
      {"&"} Training Video located in the Drive will help you and your team become familiar with the benefits of
      RoomSync and how to market and promote RoomSync at your organization.
    </Typography>
    <Typography paragraph>
      Please choose from the links below to find the appropriate resources for your organization type:
    </Typography>
    <ul>
      {OFF_CAMPUS_RESOURCES.map(({ label, url }) => (
        <li key={label}>
          <Link href={url}>
            <Typography>{label}</Typography>
          </Link>
        </li>
      ))}
    </ul>
    <ul>
      {ON_CAMPUS_RESOURCES.map(({ label, url }) => (
        <li key={label}>
          <Link href={url}>
            <Typography>{label}</Typography>
          </Link>
        </li>
      ))}
    </ul>
    <Typography paragraph>
      If you have any questions, please contact us at <Link href={`mailto:${EMAIL_ADDRESS}`}>{EMAIL_ADDRESS}</Link> or
      call {PHONE_NUMBER}.
    </Typography>
    <Typography paragraph>Welcome to RoomSync!</Typography>
  </>
);

export const FailureScreen = () => (
  <div>
    <SubPageBanner>Network Access Error</SubPageBanner>
    <StatusHolder>
      Something has gone wrong, please contact us at support@roommsync.com or call {PHONE_NUMBER}.
    </StatusHolder>
  </div>
);
